import { Component, OnInit, ViewChild, TemplateRef, NgZone, OnDestroy, Input } from '@angular/core';
import { TableConfigs, ToolBarConfigs } from 'src/app/common/components/data-table/data-table.component';
import { CreativeStatus } from 'src/app/data/constants/creative-status.const';
import { createReactTemplateElement } from '@angular-react/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ListResponse } from 'src/app/data/interfaces/ListResponse';
import { CreativeListVO } from 'src/app/data/vo/CreativeListVO';
import { CreativeAdapter } from 'src/adapters/creative-adapter';
import { CellType, ICallToActionProps } from '@amobee/component-library';
import { CreativeVO } from 'src/app/data/vo/CreativeVO';
import { FileUploaderService } from 'src/app/services/file-uploader.service';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';
import { CampaignAdapter } from 'src/adapters/campaign-adapter';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';
import { addRemoveInvisibleScroll } from 'src/app/misc/util/util';

@Component({
    selector: 'creative-list',
    templateUrl: './creative-list.component.html',
    styleUrls: ['./creative-list.component.scss']
})
export class CreativeListComponent implements OnInit, OnDestroy {

    @ViewChild('statusTemplate', { static: true }) public statusTemplate: TemplateRef<any>;
    @ViewChild('campaignTemplate', { static: true }) public campaignTemplate: TemplateRef<any>;
    @Input() reloadTableTriggered: any;
    showContentLoading: boolean = false;
    showRejectionNotesInfo: boolean = false;

    constructor(
        public ngZone: NgZone,
        private router: Router,
        private route: ActivatedRoute,
        private creativeAdapter: CreativeAdapter,
        private fileUploaderService: FileUploaderService,
        private campaignAdapter: CampaignAdapter,
        private breadcrumbService: BreadcrumbService
    ) {
    }

    ngOnInit() {
        addRemoveInvisibleScroll(true);
        this.uploadSubscription = this.fileUploaderService.getNotification().subscribe((value: any) => {
            // reload list page
            this.isDataAlreadyFetched = false;
            this.showContentLoading = true;
            this.reloadTableTriggered = {};
        });

        this.campaignAdapter.isMultiAdvertiser().then(((isMultiAdvertiser: boolean) => {
            if (isMultiAdvertiser) {
                this.campaignAdapter.getCBAId().then((resp) => {
                    this.breadcrumbService.prepareBreadcrumb(resp.customerId, resp.advertiserId);
                });
            }
        }));
    }

    ngOnDestroy(): void {
        addRemoveInvisibleScroll(false);
        if (this.uploadSubscription) {
            this.uploadSubscription.unsubscribe();
        }
    }

    private uploadSubscription: Subscription;
    public searchNotFoundCallToAction: ICallToActionProps = {
        type: 'EmptySearchResults',
        title: 'No Results Found'
    }
    public openPreview: boolean = false;
    public creative: CreativeVO;
    public isDataAlreadyFetched: boolean = false;
    public fullDataList: CreativeListVO[] = [];
    public tableConfigs: TableConfigs = {
        disableSorting: false,
        defaultSort: {
            id: 'creativeId',
            desc: true
        },
        uniqueIdAccessor: 'creativeId',
        pageSize: 25, // default to 25
        isSelectionEnabled: false,
        isPaginationEnabled: true,
        showSummaryRow: false,
        stickyHeader: true,
        stickyColumnHeaders: true,
        enableStickyColumns: true,
        fetchStaticData: (params: any) => {
            this.showContentLoading = true;
            if (this.isDataAlreadyFetched) {
                return Promise.resolve(this.doFilter(params));
            } else {
                // get full list again from api
                return this.creativeAdapter.getAllCreativeLists().then((list: CreativeListVO[]) => {
                    this.fullDataList = list;
                    this.showRejectionNotesInfo = list.findIndex(creative => creative.status === CreativeStatus.Rejected.key) > -1
                    this.isDataAlreadyFetched = true;
                    return this.doFilter(params);
                });
            }
        },
        showCallToAction: true,
        callToActionProps: this.searchNotFoundCallToAction
    };
    // TODO: Remove after creative api is supporting pagination and filters
    public doFilter(params: any): ListResponse {
        let results: any[];
        if (params.searchString && params.searchString.length > 0) {
            let searchString = params.searchString.toLowerCase();
            results = this.fullDataList.filter((creative: CreativeListVO) => {
                return creative.name.toLowerCase().includes(searchString) || creative.creativeId.toString().includes(searchString);
            });
        } else {
            results = [].concat(this.fullDataList);
        }
        if (params.columnKey) {
            let order = params.sortOrder ? params.sortOrder.toLowerCase() : 'desc';
            results = _.orderBy(results, [params.columnKey], [order])
        }

        this.showContentLoading = false;
        return {
            results: results.slice(params.offset, params.offset + params.limit),
            count: results.length
        }
    }

    public columns: any[] = [
        {
            Header: 'Name',
            accessor: 'name',
            id: 'name',
            isRowActionColumn: true,
            sticky: 'left',
            cellType: CellType.LINK,
            isActive: () => true,
            onLinkClick: (row) => {
                this.openPreview = true;
                this.creative = row.original;
            }
        },
        {
            Header: 'Status',
            accessor: row => {
                // hide return icon property currently until we have icon defined for every plan status
                return { status: CreativeStatus[row.status], creativeStatusNotes: row.creativeStatusNotes };
            },
            id: 'status',
            Cell: (props) => {
                return createReactTemplateElement(this.statusTemplate, props.cell, this.ngZone);
            },
            width: 120
        },
        {
            Header: 'ID',
            accessor: 'creativeId',
            id: 'creativeId',
            width: 100
        },
        {
            Header: 'IBS Video ID',
            accessor: 'externalIdentifier',
            id: 'externalIdentifier',
            width: 120
        },
        {
            Header: 'Assigned',
            accessor: 'campaigns',
            id: 'campaigns',
            disableSortBy: true,
            Cell: (props) => {
                return createReactTemplateElement(this.campaignTemplate, props.cell, this.ngZone);
            },
            width: 120
        },
        {
            Header: 'Type',
            accessor: 'type',
            id: 'type',
            width: 100
        },
        {
            Header: 'Language',
            accessor: 'language',
            id: 'language',
            width: 100
        },
        {
            Header: 'Duration (seconds)',
            accessor: 'adDuration',
            id: 'adDuration',
            width: 120
        }
    ];

    public toolbarConfigs: ToolBarConfigs = {
        toolbarOptions: {
            isAddButtonVisible: true,
            onAddClick: () => {
                this.showContentLoading = true;
                this.router.navigate(['..', 'new'], { relativeTo: this.route, queryParams: { isVideoProduce: false } }).catch(() => {
                    this.showContentLoading = false;
                });
            },
            additionalButtons: [
                {
                    icon: 'AutoFix',
                    onClick: () => {
                        this.showContentLoading = true;
                        this.router.navigate(['..', 'new'], { relativeTo: this.route, queryParams: { isVideoProduce: true } }).catch(() => {
                            this.showContentLoading = false;
                        });
                    },
                    title: `Produce a creative with ${environment.platform}`
                }
            ]
        },
        isSearchEnable: true
    };

    closePreview() {
        this.openPreview = false;
        this.creative = null;
    }

}
